/* eslint-disable no-console */

import { ActivationState, Client, StompSocketState } from '@stomp/stompjs';
import SockJs from 'sockjs-client';

import configs from '@aqx-core/common/configs';
import {
  ChartTopic,
  DOMTopic,
  DealerTopic,
  MetaTraderTopic,
  NewsTopic,
  OptionTopic,
  PriceLadderTopic,
  PublicTopic,
  QuoteTopic,
  SignalTopic,
  SymbolTopic,
  TradeTopic,
  TradingStatusTopic,
  UserTopic
} from '@aqx-core/common/socket/topics';
import storage, { EStorageKey } from '@aqx-core/common/storage';

const SOCKET_SERVER = `${configs.socketPathPrefix}/websocket`;

interface IWebsocketTopics {
  metatrader: MetaTraderTopic;
  news: NewsTopic;
  public: PublicTopic;
  signal: SignalTopic;
  symbol: SymbolTopic;
  priceLadder: PriceLadderTopic;
  option: OptionTopic;
  quote: QuoteTopic;
  trade: TradeTopic;
  tradingStatus: TradingStatusTopic;
  chart: ChartTopic;
  dealer: DealerTopic;
  dom: DOMTopic;
  user: UserTopic;
}

class WebSocketService {
  private client: Client;
  private socketTopics: IWebsocketTopics;
  private previouslyConnected = false;
  private reconnectListeners: Set<() => void> = new Set();

  private onConnect?: (state?: StompSocketState) => void;
  private onDisconnect?: (state?: StompSocketState) => void;

  constructor() {
    this.client = new Client({
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,

      webSocketFactory: () => {
        console.log('[SOCKET_SERVER IN WEB]', SOCKET_SERVER);
        return new SockJs(SOCKET_SERVER);
      },
      debug: () => {
        // console.log('[Socket Log]', message);
      },
      beforeConnect: () => {
        this.onReceiveState(this.client?.webSocket?.readyState);
      },
      onConnect: (payload) => {
        console.log('[onConnect in WEB]', payload);
        const state = this.client?.webSocket?.readyState;

        if (this.previouslyConnected) {
          // MARK: Read this - Remember to reconnect all the subscriptions when the socket is reconnected
          this.socketTopics.symbol.reconnect();
          this.socketTopics.trade.reconnect();
          this.socketTopics.quote.reconnect();
          this.socketTopics.option.reconnect();
          this.socketTopics.priceLadder.reconnect();
          this.socketTopics.metatrader.reconnect();
          this.socketTopics.tradingStatus.reconnect();
          this.socketTopics.chart.reconnect();
          this.socketTopics.signal.reconnect();
          this.socketTopics.dealer.reconnect();
          this.socketTopics.dom.reconnect();
          // Reconnect all the listeners
          this.handleReconnect();
        } else {
          this.previouslyConnected = true;
        }
        this.onConnect?.(state);
        this.onReceiveState(state);
      },
      onDisconnect: (payload) => {
        console.log('[onDisconnect in WEB]', payload);
        const state = this.client?.webSocket?.readyState;

        this.onDisconnect?.(state);
        this.onReceiveState(this.client?.webSocket?.readyState);
      },
      onStompError: (receipt) => {
        console.log('[onStompError in WEB]', receipt);
      },
      onWebSocketClose: () => {
        console.log('Web socket close in WEB');
        this.onReceiveState(this.client?.webSocket?.readyState);
      },
      onWebSocketError: (e) => {
        console.error('Web socket error in WEB:', e);
        this.onReceiveState(this.client?.webSocket?.readyState);
      }
    });

    this.socketTopics = {
      news: new NewsTopic({
        client: this.client
      }),
      signal: new SignalTopic({
        client: this.client
      }),
      symbol: new SymbolTopic({
        client: this.client
      }),
      public: new PublicTopic({
        client: this.client
      }),
      metatrader: new MetaTraderTopic({
        client: this.client
      }),
      priceLadder: new PriceLadderTopic({
        client: this.client
      }),
      option: new OptionTopic({
        client: this.client
      }),
      quote: new QuoteTopic({
        client: this.client
      }),
      trade: new TradeTopic({
        client: this.client
      }),
      tradingStatus: new TradingStatusTopic({
        client: this.client
      }),
      chart: new ChartTopic({
        client: this.client
      }),
      dealer: new DealerTopic({
        client: this.client
      }),
      dom: new DOMTopic({
        client: this.client
      }),
      user: new UserTopic({
        client: this.client
      })
    };
  }

  public topics = () => {
    return this.socketTopics;
  };

  public state = () => {
    return this.client.state;
  };

  public connect = (onConnect?: (state?: StompSocketState) => void) => {
    if (!this.client) {
      return;
    }

    if (onConnect) {
      this.onConnect = onConnect;
    }

    const token = storage.getItem(EStorageKey.TOKEN);

    if (token) {
      this.client.connectHeaders = {
        token
      };
    }

    this.client.activate();
  };

  public disconnect = (onDisconnect?: (state?: StompSocketState) => void) => {
    if (onDisconnect) {
      this.onDisconnect = onDisconnect;
    }

    this.clearSubscriptions();

    this.previouslyConnected = false;

    // Clear all the reconnect listeners
    this.reconnectListeners.clear();

    this.client.deactivate();
  };

  public addReconnectListener(listener: () => void) {
    this.reconnectListeners.add(listener);
  }

  public removeReconnectListener(listener: () => void) {
    this.reconnectListeners.delete(listener);
  }

  private handleReconnect() {
    this.reconnectListeners.forEach((listener) => listener());
  }

  private clearSubscriptions = () => {
    const { socketTopics } = this;

    // MARK: Read this - Remember to clear all the subscriptions when the socket is disconnect
    Object.values(socketTopics).forEach((socket: IWebsocketTopics[keyof typeof socketTopics]) => {
      socket.clearSubscriptions();
    });
  };

  private onReceiveState = (state?: StompSocketState) => {
    console.log('Received state:', state);
  };

  public viewSocketConnect = async () => {
    return new Promise<boolean>((resolve) => {
      const onSocketSuccess = () => {
        resolve(true);
      };

      if (this.state() === ActivationState.ACTIVE) {
        onSocketSuccess();
      } else {
        this.connect((state) => {
          if (state === StompSocketState.OPEN) {
            onSocketSuccess();
          }
        });
      }
    });
  };

  /** Function to manually close the socket - only for testing */
  // public close = () => {
  //   if (this.client && this.client.connected) {
  //     // this.client.deactivate();
  //     this.client.webSocket.close();
  //     this.previouslyConnected = true; // Set this to true so it will reconnect on next activation
  //   }
  // };
}

export const webSocketService = new WebSocketService();
